/**
 * Skeleton Loader per Sasha Player
 * 
 * Evita CLS fornendo placeholder con dimensioni fisse
 * Responsive design che si adatta ai breakpoint del player
 * 
 * @since v3.1.0 - Web Vitals optimization
 */

export class SkeletonLoader {
  /**
   * Crea skeleton HTML con dimensioni responsive
   * 
   * @param {Object} config - Configurazione player
   * @returns {string} HTML skeleton
   */
  static createSkeleton(config = {}) {
    const hasCover = config.coverImage;
    const hasSponsor = config.sponsorLogo;
    
    return `
      <div class="sasha-skeleton">
        <div class="sasha-skeleton-container">
          ${hasCover ? this.createCoverSkeleton() : ''}
          <div class="sasha-skeleton-content">
            <div class="sasha-skeleton-header">
              <div class="sasha-skeleton-title"></div>
              <div class="sasha-skeleton-subtitle"></div>
            </div>
            <div class="sasha-skeleton-waveform"></div>
            <div class="sasha-skeleton-controls">
              <div class="sasha-skeleton-play"></div>
              <div class="sasha-skeleton-progress"></div>
              <div class="sasha-skeleton-volume"></div>
            </div>
          </div>
          ${hasSponsor ? this.createSponsorSkeleton() : ''}
        </div>
      </div>
    `;
  }
  
  /**
   * Crea skeleton per cover image
   */
  static createCoverSkeleton() {
    return `
      <div class="sasha-skeleton-cover">
        <div class="sasha-skeleton-play-overlay"></div>
      </div>
    `;
  }
  
  /**
   * Crea skeleton per sponsor logo
   */
  static createSponsorSkeleton() {
    return `
      <div class="sasha-skeleton-sponsor">
        <div class="sasha-skeleton-sponsor-logo"></div>
        <div class="sasha-skeleton-sponsor-text"></div>
      </div>
    `;
  }
  
  /**
   * CSS per skeleton loading
   */
  static getSkeletonCSS() {
    return `
      .sasha-skeleton {
        display: block;
        width: 100%;
        min-height: 180px;
        background: linear-gradient(90deg, 
          rgba(240, 240, 240, 0.3) 25%, 
          rgba(224, 224, 224, 0.4) 50%, 
          rgba(240, 240, 240, 0.3) 75%
        );
        background-size: 200% 100%;
        animation: skeleton-loading 1.5s infinite;
        border-radius: 16px;
        overflow: hidden;
        backdrop-filter: blur(8px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
      }
      
      .sasha-skeleton-container {
        display: flex;
        flex-direction: row;
        align-items: stretch;
        height: 100%;
        min-height: 180px;
        background: rgba(26, 26, 46, 0.6);
      }
      
      .sasha-skeleton-cover {
        width: 180px;
        height: 180px;
        background: linear-gradient(45deg, 
          rgba(224, 224, 224, 0.25), 
          rgba(240, 240, 240, 0.35)
        );
        position: relative;
        flex-shrink: 0;
      }
      
      .sasha-skeleton-play-overlay {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 60px;
        height: 60px;
        background: rgba(255, 255, 255, 0.5);
        border-radius: 50%;
        backdrop-filter: blur(4px);
      }
      
      .sasha-skeleton-content {
        flex: 1;
        padding: 20px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
      
      .sasha-skeleton-title {
        height: 24px;
        background: linear-gradient(90deg, 
          rgba(208, 208, 208, 0.35), 
          rgba(224, 224, 224, 0.45)
        );
        border-radius: 4px;
        margin-bottom: 8px;
        width: 70%;
        backdrop-filter: blur(2px);
      }
      
      .sasha-skeleton-subtitle {
        height: 16px;
        background: linear-gradient(90deg, 
          rgba(208, 208, 208, 0.3), 
          rgba(224, 224, 224, 0.4)
        );
        border-radius: 4px;
        width: 50%;
        backdrop-filter: blur(2px);
      }
      
      .sasha-skeleton-waveform {
        height: 48px;
        background: linear-gradient(90deg, 
          rgba(208, 208, 208, 0.25), 
          rgba(224, 224, 224, 0.35)
        );
        border-radius: 8px;
        margin: 16px 0;
        backdrop-filter: blur(2px);
      }
      
      .sasha-skeleton-controls {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      
      .sasha-skeleton-play {
        width: 40px;
        height: 40px;
        background: linear-gradient(45deg, 
          rgba(208, 208, 208, 0.4), 
          rgba(224, 224, 224, 0.5)
        );
        border-radius: 50%;
        backdrop-filter: blur(2px);
      }
      
      .sasha-skeleton-progress {
        flex: 1;
        height: 6px;
        background: linear-gradient(90deg, 
          rgba(208, 208, 208, 0.3), 
          rgba(224, 224, 224, 0.4)
        );
        border-radius: 3px;
        backdrop-filter: blur(2px);
      }
      
      .sasha-skeleton-volume {
        width: 100px;
        height: 4px;
        background: linear-gradient(90deg, 
          rgba(208, 208, 208, 0.3), 
          rgba(224, 224, 224, 0.4)
        );
        border-radius: 2px;
        backdrop-filter: blur(2px);
      }
      
      .sasha-skeleton-sponsor {
        padding: 16px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        min-width: 120px;
      }
      
      .sasha-skeleton-sponsor-logo {
        width: 48px;
        height: 48px;
        background: linear-gradient(45deg, 
          rgba(208, 208, 208, 0.35), 
          rgba(224, 224, 224, 0.45)
        );
        border-radius: 8px;
        backdrop-filter: blur(2px);
      }
      
      .sasha-skeleton-sponsor-text {
        height: 12px;
        background: linear-gradient(90deg, 
          rgba(208, 208, 208, 0.3), 
          rgba(224, 224, 224, 0.4)
        );
        border-radius: 4px;
        width: 80px;
        backdrop-filter: blur(2px);
      }
      
      @keyframes skeleton-loading {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      
      /* Responsive breakpoints */
      @media (max-width: 768px) {
        .sasha-skeleton {
          min-height: 200px;
        }
        
        .sasha-skeleton-container {
          flex-direction: column;
          min-height: 200px;
        }
        
        .sasha-skeleton-cover {
          width: 100%;
          height: 200px;
        }
      }
      
      @media (max-width: 480px) {
        .sasha-skeleton {
          height: 180px;
          min-height: 180px;
        }
        
        .sasha-skeleton-container {
          height: 180px;
          min-height: 180px;
        }
        
        .sasha-skeleton-cover {
          height: 180px;
        }
      }
    `;
  }
}
